import React, { useState, useEffect, useCallback, useMemo } from "react";
import { InputAdornment, TextField } from "@material-ui/core";
import { Search as SearchIcon } from "@material-ui/icons";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { makeStyles } from "@material-ui/core/styles";
import useHttpErrorHandler from "src/hooks/useHttpErrorHandler";
import { useDebounce } from "src/utils";

const useStyles = makeStyles(_ => ({
  searchTerm: {
    "& .MuiOutlinedInput-adornedEnd": {
      paddingRight: "16px !important"
    }
  }
}));

interface Props<T, SearchReq extends IPaginateReq<OptionKey>, OptionKey> {
  fetchDropdown: (search: SearchReq) => Promise<IResponse<IDropdown<T>[]>>;
  label: string;
  value?: IDropdown<T> | null;
  onChange: (value: IDropdown<T> | null) => void;
  paginate: IPaginate<OptionKey>;
  additionalReqParams?: Omit<SearchReq, keyof IPaginateReq<OptionKey>>;
  excludedDropdownValues?: string[];
  disabled?: boolean;
  error?: boolean;
  helperText?: React.ReactNode;
  className?: string;
  size?: "small" | "medium";
}

type StateTypes<T> = {
  fetching: boolean;
  options: IDropdown<T>[];
};

export default function AsyncDropdown<
  T,
  SearchReq extends IPaginateReq<OptionKey>,
  OptionKey
>(props: Props<T, SearchReq, OptionKey>) {
  const {
    fetchDropdown,
    label,
    onChange,
    value,
    paginate,
    additionalReqParams,
    excludedDropdownValues,
    disabled,
    error,
    helperText,
    className,
    size
  } = props;
  const classes = useStyles();
  const { handleError } = useHttpErrorHandler();

  const [open, setOpen] = React.useState(false);
  const [state, setState] = useState<StateTypes<T>>({
    fetching: false,
    options: []
  });
  const { fetching, options } = state;

  const [searchTerm, setSearchTerm] = useState("");
  const { value: debouncedSearchTerm } = useDebounce(searchTerm, 300);
  const excluded = useMemo(
    () => new Set(excludedDropdownValues ?? []),
    [excludedDropdownValues]
  );

  const handleChangeInput = (
    _: React.ChangeEvent<unknown>,
    changedSearchTerm: string
  ) => {
    setSearchTerm(changedSearchTerm);
  };

  const handleOnChange = (
    _: React.ChangeEvent<unknown>,
    changedValue: IDropdown<T> | null
  ) => {
    onChange(changedValue);
  };

  const handleFetchDropdown = useCallback(
    async (
      changedSearchTerm?: string,
      otherParams?: Omit<SearchReq, keyof IPaginateReq<OptionKey>>
    ) => {
      setState({ fetching: true, options: [] });

      try {
        const resp = await fetchDropdown({
          ...otherParams,
          paginate,
          searchTerm: changedSearchTerm
        } as SearchReq);
        if (excluded.size > 0) {
          setState({
            fetching: false,
            options: resp.value.filter(v => !excluded.has(v.value))
          });
        } else {
          setState({ fetching: false, options: resp.value });
        }
      } catch (e) {
        handleError(e);
        setState({ fetching: false, options: [] });
      }
    },
    [fetchDropdown, handleError, paginate, excluded]
  );

  useEffect(() => {
    if (open) {
      handleFetchDropdown(debouncedSearchTerm, additionalReqParams);
    }
  }, [open, handleFetchDropdown, debouncedSearchTerm, additionalReqParams]);

  const isLoading = fetching;

  return (
    <Autocomplete
      className={className}
      fullWidth
      open={open}
      onOpen={() => {
        setOpen(true);
      }}
      onClose={() => {
        setOpen(false);
      }}
      getOptionSelected={(option, optionValue) =>
        option.value === optionValue.value
      }
      getOptionLabel={option => option.label}
      disabled={disabled}
      options={options}
      loading={isLoading}
      loadingText="Loading..."
      onInputChange={handleChangeInput}
      onChange={handleOnChange}
      value={value}
      renderInput={params => (
        <TextField
          className={classes.searchTerm}
          {...params}
          disabled={disabled}
          label={label}
          error={error}
          helperText={helperText}
          variant="outlined"
          size={size}
          placeholder={`พิมพ์เพื่อค้นหา ${label}`}
          value=""
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <InputAdornment position="end">
                <SearchIcon />
              </InputAdornment>
            )
          }}
        />
      )}
    />
  );
}
